/**
 * Spotlight hook — maps visible spotlight texts to line indices.
 *
 * Searches the raw document body text for **all** occurrences of each
 * spotlight quote, then maps match positions back to line indices.
 *
 * Returns:
 *  - Set of spotlighted line indices for visual highlighting
 *  - Match count reported to the spotlight context for the scrubber bar
 *  - Scroll-to-match on jump requests, including prev/next navigation
 */

import {useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import {useDocumentSpotlight} from '../../../../../contexts/document_spotlight_context';
import {
    stripGutter, fullyNormalize,
    findFullyNormalized as sharedFindFullyNormalized,
} from './text-matching';

const SPOTLIGHT_MATCH_CLASS = 'spotlight-match';

export interface LineSpotlightState {
    /** Set of line indices that have a spotlight match */
    spotlightedLines: Set<number>;
    /** CSS class to add to spotlighted line elements */
    spotlightClass: string;
    /** Ref to attach to the scroll container */
    containerRef: React.RefObject<HTMLDivElement>;
    /** Jump to next match in the text viewer */
    jumpToNextMatch: () => void;
    /** Jump to previous match in the text viewer */
    jumpToPreviousMatch: () => void;
}

/** A single match: the range of line indices it covers */
export interface MatchRange {
    startLine: number;
    endLine: number;
}

/**
 * Find ALL occurrences of `needle` in `haystack` (case-insensitive),
 * returning the char index of each hit.
 */
function findAllOccurrences(haystack: string, needle: string): number[] {
    if (!needle || !haystack) return [];
    const results: number[] = [];
    const lowerHay = haystack.toLowerCase();
    const lowerNeedle = needle.toLowerCase();
    let pos = 0;
    while (pos < lowerHay.length) {
        const idx = lowerHay.indexOf(lowerNeedle, pos);
        if (idx === -1) break;
        results.push(idx);
        pos = idx + 1; // allow overlapping matches
    }
    return results;
}

/** Convert a char index in rawBodyText to a line index (0-based). */
function charIndexToLine(rawBodyText: string, charIdx: number): number {
    return (rawBodyText.slice(0, charIdx).match(/\n/g) || []).length;
}

export function useLineSpotlight(
    rawBodyText: string,
    lineCount: number,
): LineSpotlightState {
    const spotlightCtx = useDocumentSpotlight();
    const containerRef = useRef<HTMLDivElement>(null);
    const currentMatchRef = useRef(0);
    const lastHandledJumpSeq = useRef(0);
    // Ref to avoid re-creating scrollToMatch when context re-renders (which
    // would re-trigger the jumpRequest effect and snap back to match 0)
    const spotlightCtxRef = useRef(spotlightCtx);
    spotlightCtxRef.current = spotlightCtx;

    const findFullyNormalized = useCallback(
        (haystack: string, needle: string) => sharedFindFullyNormalized(haystack, needle),
        [],
    );

    // Find all match ranges for all visible spotlights.
    // Also track per-spotlight ranges so we can report accurate counts for the active one.
    const activeId = spotlightCtx?.activeSpotlightId ?? null;
    const {spotlightedLines, matchRanges, activeMatchRanges} = useMemo(() => {
        const lines = new Set<number>();
        const ranges: MatchRange[] = [];
        const activeRanges: MatchRange[] = [];
        const visibleSpotlights = (spotlightCtx?.spotlights ?? []).filter(s => s.visible);
        if (!visibleSpotlights.length || !rawBodyText) return {spotlightedLines: lines, matchRanges: ranges, activeMatchRanges: activeRanges};

        // Build stripped body once (line numbers removed)
        const strippedBody = stripGutter(rawBodyText);

        for (const spotlight of visibleSpotlights) {
            const needle = spotlight.text;
            if (!needle) continue;
            const isActive = spotlight.id === activeId;
            const rangesForThis: MatchRange[] = [];

            // Speaker or line spotlight: use pre-computed matchItems (line indices)
            if ((spotlight.kind === 'speaker' || spotlight.kind === 'line')
                && spotlight.matchItems && spotlight.matchItems.length > 0) {
                for (const item of spotlight.matchItems) {
                    const r = {startLine: item.lineIndex, endLine: item.lineIndex};
                    rangesForThis.push(r);
                    lines.add(item.lineIndex);
                }
                ranges.push(...rangesForThis);
                if (isActive) activeRanges.push(...rangesForThis);
                continue;
            }

            // --- Matching pipeline (most specific → most tolerant) ---

            let matched = false;

            // 1. Exact match on raw body
            const exactHits = findAllOccurrences(rawBodyText, needle);
            if (exactHits.length > 0) {
                const nlCount = (needle.match(/\n/g) || []).length;
                for (const idx of exactHits) {
                    const startLine = charIndexToLine(rawBodyText, idx);
                    const endLine = Math.min(startLine + nlCount, lineCount - 1);
                    const r = {startLine, endLine};
                    rangesForThis.push(r);
                    for (let l = startLine; l <= endLine; l++) lines.add(l);
                }
                matched = true;
            }

            // 2. Fully normalized match (strips gutters + collapses whitespace + lowercases).
            if (!matched) {
                const normHits = findFullyNormalized(rawBodyText, needle);
                if (normHits.length > 0) {
                    const nlCount = (stripGutter(needle).match(/\n/g) || []).length;
                    for (const idx of normHits) {
                        const startLine = charIndexToLine(strippedBody, idx);
                        const endLine = Math.min(startLine + nlCount, lineCount - 1);
                        const r = {startLine, endLine};
                        rangesForThis.push(r);
                        for (let l = startLine; l <= endLine; l++) lines.add(l);
                    }
                    matched = true;
                }
            }

            // 3. Aggressive number stripping — remove ALL standalone 1-3 digit numbers
            //    from the needle. Handles LLM quotes with scattered line numbers like
            //    "19 20 21 22 A No. Q I take it" where numbers aren't at line starts.
            if (!matched) {
                const nukeNumbers = (s: string) => s.replace(/\b\d{1,3}\b/g, '').replace(/\s+/g, ' ').trim();
                const nukeedNeedle = nukeNumbers(fullyNormalize(needle));
                const nukeedHay = nukeNumbers(fullyNormalize(rawBodyText));
                if (nukeedNeedle.length >= 10) {
                    let pos = 0;
                    const nukeedHits: number[] = [];
                    while (pos < nukeedHay.length) {
                        const idx = nukeedHay.indexOf(nukeedNeedle, pos);
                        if (idx === -1) break;
                        nukeedHits.push(idx);
                        pos = idx + 1;
                    }
                    if (nukeedHits.length > 0 && nukeedHits.length <= 5) {
                        // Approximate: map fraction of nukeedHay position back to strippedBody position
                        const ratio = strippedBody.length / (nukeedHay.length || 1);
                        for (const idx of nukeedHits) {
                            const approxCharIdx = Math.round(idx * ratio);
                            const startLine = charIndexToLine(strippedBody, approxCharIdx);
                            const r = {startLine, endLine: startLine};
                            rangesForThis.push(r);
                            lines.add(startLine);
                        }
                        matched = true;
                    }
                }
            }

            // 4. Partial match — try the first ~60% of the quote (handles LLM truncation
            //    or slight ending differences). Only if the needle is long enough.
            if (!matched && needle.length > 40) {
                const partialLen = Math.max(30, Math.floor(needle.length * 0.6));
                const partial = needle.slice(0, partialLen);
                const partialHits = findFullyNormalized(rawBodyText, partial);
                if (partialHits.length > 0) {
                    for (const idx of partialHits) {
                        const startLine = charIndexToLine(strippedBody, idx);
                        const r = {startLine, endLine: startLine};
                        rangesForThis.push(r);
                        lines.add(startLine);
                    }
                    matched = true;
                }
            }

            ranges.push(...rangesForThis);
            if (isActive) activeRanges.push(...rangesForThis);
        }

        // Sort ranges by document order for prev/next navigation
        ranges.sort((a, b) => a.startLine - b.startLine);
        activeRanges.sort((a, b) => a.startLine - b.startLine);

        return {spotlightedLines: lines, matchRanges: ranges, activeMatchRanges: activeRanges};
    }, [rawBodyText, lineCount, spotlightCtx?.spotlights, activeId, findFullyNormalized]);

    // Navigation ranges: use active spotlight's matches when available,
    // fall back to all matches when no spotlight is active.
    const navRanges = activeMatchRanges.length > 0 ? activeMatchRanges : matchRanges;

    // Report match count synchronously before paint — only for the active spotlight
    useLayoutEffect(() => {
        spotlightCtx?.updateMatchInfo(0, navRanges.length);
        currentMatchRef.current = 0;
    }, [navRanges.length]);

    // Scroll to a specific match index.
    // Uses spotlightCtxRef (not spotlightCtx) to avoid re-creating this callback
    // when the context re-renders — otherwise updateMatchInfo → context re-render →
    // new scrollToMatch → jumpRequest effect re-fires → snaps back to match 0.
    const scrollToMatch = useCallback((matchIdx: number) => {
        if (!containerRef.current || navRanges.length === 0) return;
        const idx = ((matchIdx % navRanges.length) + navRanges.length) % navRanges.length;
        currentMatchRef.current = idx;
        spotlightCtxRef.current?.updateMatchInfo(idx, navRanges.length);

        const targetLine = navRanges[idx].startLine;
        const allSpotlightEls = containerRef.current.querySelectorAll(`.${SPOTLIGHT_MATCH_CLASS}`);
        // Find the element closest to the target line — each spotlight-match element
        // should correspond to a spotlighted line row.
        let bestEl: HTMLElement | null = null;
        let bestDist = Infinity;
        allSpotlightEls.forEach(el => {
            const lineIdx = parseInt((el as HTMLElement).dataset.lineIndex ?? '-1', 10);
            const dist = Math.abs(lineIdx - targetLine);
            if (dist < bestDist) {
                bestDist = dist;
                bestEl = el as HTMLElement;
            }
        });

        if (bestEl) {
            (bestEl as HTMLElement).style.scrollMarginTop = '4rem';
            (bestEl as HTMLElement).scrollIntoView({behavior: 'smooth', block: 'start'});
            setTimeout(() => { (bestEl as HTMLElement).style.scrollMarginTop = ''; }, 600);
        }
    }, [navRanges]);

    const jumpToNextMatch = useCallback(() => {
        scrollToMatch(currentMatchRef.current + 1);
    }, [scrollToMatch]);

    const jumpToPreviousMatch = useCallback(() => {
        scrollToMatch(currentMatchRef.current - 1);
    }, [scrollToMatch]);

    // Register text navigation with the spotlight context so the scrubber bar works
    useEffect(() => {
        if (!spotlightCtx?.textNavRef) return;
        spotlightCtx.textNavRef.current = {jumpToNextMatch, jumpToPreviousMatch};
        return () => {
            if (spotlightCtx.textNavRef.current?.jumpToNextMatch === jumpToNextMatch) {
                spotlightCtx.textNavRef.current = null;
            }
        };
    }, [jumpToNextMatch, jumpToPreviousMatch, spotlightCtx]);

    // Scroll to first match when a NEW jump request arrives.
    // Uses lastHandledJumpSeq to ensure each jump only fires once —
    // prevents re-triggering when scrollToMatch/matchRanges are recreated.
    const jumpRequest = spotlightCtx?.jumpRequest;
    useEffect(() => {
        if (!jumpRequest || !containerRef.current) return;
        if (jumpRequest.seq <= lastHandledJumpSeq.current) return;
        lastHandledJumpSeq.current = jumpRequest.seq;

        // Page spotlight: scroll to the first line on that page via data-page attribute
        const spotlight = (spotlightCtx?.spotlights ?? []).find(s => s.text === jumpRequest.text);
        if (spotlight?.kind === 'page' && spotlight.pageHint != null) {
            const el = containerRef.current.querySelector(`[data-page="${spotlight.pageHint}"]`);
            if (el) {
                (el as HTMLElement).style.scrollMarginTop = '4rem';
                el.scrollIntoView({behavior: 'smooth', block: 'start'});
                setTimeout(() => { (el as HTMLElement).style.scrollMarginTop = ''; }, 600);
            }
            return;
        }

        if (navRanges.length === 0) return;
        scrollToMatch(0);
    }, [jumpRequest, navRanges, scrollToMatch]);

    // Direct page jump (no spotlight) — used by examination blocks
    const pageJumpRequest = spotlightCtx?.pageJumpRequest;
    useEffect(() => {
        if (!pageJumpRequest || !containerRef.current) return;
        const el = containerRef.current.querySelector(`[data-page="${pageJumpRequest.page}"]`);
        if (el) {
            (el as HTMLElement).style.scrollMarginTop = '4rem';
            el.scrollIntoView({behavior: 'smooth', block: 'start'});
            setTimeout(() => { (el as HTMLElement).style.scrollMarginTop = ''; }, 600);
        }
    }, [pageJumpRequest]);

    return {
        spotlightedLines,
        spotlightClass: SPOTLIGHT_MATCH_CLASS,
        containerRef: containerRef as React.RefObject<HTMLDivElement>,
        jumpToNextMatch,
        jumpToPreviousMatch,
    };
}
